/**
 * RoleEditor.tsx — 角色说明弹窗（4 角色闭集只读，原 6 角色 / 13 权限矩阵编辑器已裁撤）
 *
 * 契约裁决（P0-admin-auth-rbac §4 + docs/plans/2026-06-12-web-real-api-integration-plan.md §五）：
 * 后端 RBAC 是 4 角色闭集（root/admin/operator/viewer）写死，无任何角色 CRUD / 自定义权限矩阵端点。
 * 原型「新建角色 / 自定义 13 权限矩阵」杜撰了后端不存在的 RBAC 模型 → 本弹窗改为 4 角色**只读说明**，
 * 不渲染可编辑的权限勾选 / 「允许·禁止」分段控件 / 保存按钮（避免假装可改注定无端点的权限）。
 * 角色范围文案对齐 P0-admin-auth-rbac §4 角色表。
 */

import { Dialog } from '~/ui/Dialog';

/** 角色对象（与 SettingsPage Roles 区块的 ROLES 形状一致）*/
export interface RoleItem {
  id: 'root' | 'admin' | 'operator' | 'viewer';
  name: string;
  members?: number;
  color?: string;
  desc?: string;
  scope: string;
}

/** 4 角色闭集职责说明（对齐 P0-admin-auth-rbac §4）*/
const ROLE_DETAIL: Record<RoleItem['id'], string[]> = {
  root: [
    '全部权限',
    '运营账号管理（邀请 / 改角色 / 移除，/admin-users/*）',
    '设备白名单（/users/device-whitelist）',
    'BONUS 发放（/finance/grant-bonus）',
  ],
  admin: ['全部权限（不含 root 专属：账号管理 / 设备白名单 / BONUS 发放）'],
  operator: ['书籍 / 章节 / 推送 / 反馈 / Banner / FAQ 日常运营'],
  viewer: ['仅读：所有列表 + 详情可看', '所有写接口一律拒绝'],
};

export function RoleEditor({ role, onClose }: { role?: RoleItem; onClose: () => void }) {
  return (
    <Dialog
      width={560}
      title={role ? `角色说明 · ${role.name}` : '角色与权限'}
      subtitle="系统采用固定 4 角色闭集（root / admin / operator / viewer），不支持自定义角色"
      icon="IconShield"
      onClose={onClose}
      footer={
        <button className="btn" onClick={onClose} style={{ marginLeft: 'auto' }}>
          关闭
        </button>
      }
    >
      <div style={{ padding: 18 }}>
        <div
          className="hstack"
          style={{
            gap: 8,
            padding: 12,
            background: 'var(--bg-subtle)',
            borderRadius: 6,
            marginBottom: 14,
            fontSize: 11.5,
            color: 'var(--fg-muted)',
            lineHeight: 1.55,
          }}
        >
          角色由后端权限模型固定，无法新建 / 删除 / 改权限矩阵；可在「团队成员」处把成员设为这 4
          个角色之一。
        </div>
        {(role ? [role.id] : (['root', 'admin', 'operator', 'viewer'] as RoleItem['id'][])).map(
          (rid) => {
            const NAME = { root: 'Root', admin: 'Admin', operator: '运营', viewer: '只读' }[rid];
            return (
              <div key={rid} className="card-flat" style={{ padding: 12, marginBottom: 8 }}>
                <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 6 }}>{NAME}</div>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 18,
                    fontSize: 12,
                    color: 'var(--fg-muted)',
                    lineHeight: 1.7,
                  }}
                >
                  {ROLE_DETAIL[rid].map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            );
          },
        )}
      </div>
    </Dialog>
  );
}
